import { useState, useEffect } from "react";
import MainLayouts from "../../../Layout/MainLayouts";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import { ReactComponent as ArrowLeftSquareFill } from "bootstrap-icons/icons/arrow-left-square-fill.svg";
import { useNavigate, useLocation } from "react-router-dom";
import CurrencyFormatter from "../../../components/utils/CurrencyFormatter";
import formatDate from "../../../components/utils/DateFormatter";
import Spinner from "../../spinner/Spinner";

const BranchProductDetails = () => {
  const [productData, setProductData] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { data } = location.state || {};

  useEffect(() => {
    if (data) {
      setProductData(data);
      // console.log("Product Data:", data);
      setLoading(false);
    } else {
      // Redirect back if no data
      navigate(-1);
    }
  }, [data, navigate]);

  if (loading || !productData) {
    return (
      <MainLayouts>
        <Spinner />
      </MainLayouts>
    );
  }

  const InfoCard = ({ title, children }) => (
    <div className="bg-white rounded-lg shadow-sm border border-lGrey p-3 mb-6">
      <h2 className="text-lg font-semibold  text-lGrey mb-4 pb-2 border-b border-lGrey bg-darkGreen3 px-1">
        {title}
      </h2>
      {children}
    </div>
  );

  const InfoRow = ({ label, value, highlight = false }) => (
    <div className="flex justify-between py-2.5 border-b border-gray-100 last:border-0">
      <span className="text-gray-600 text-sm font-medium">{label}:</span>
      <span className={`text-right text-sm ${highlight ? 'font-semibold text-darkGreen3' : 'text-gray-800'}`}>
        {value || "0"}
      </span>
    </div>
  );

  return (
    <div>
      <MainLayouts>
        <div className="relative overflow-x-auto sm:rounded-lg py-6 w-full">
          <section className="w-full pr-[3%] pt-3 my-3" style={{ backgroundColor: "#F7F7F7" }}>
            <nav className="bg-gray-200 py-0 px-4 rounded-lg">
              <ol className="list-none p-0 inline-flex mb-2">
                <li className="flex items-center">
                  <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                    Operations
                  </p>
                  <ChevronRightIcon />
                </li>
                <li className="flex items-center">
                  <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                    Branch
                  </p>
                  <ChevronRightIcon />
                </li>
                <li className="flex items-center">
                  <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                    Stock
                  </p>
                  <ChevronRightIcon />
                </li>
                <li className="flex items-center">
                  <p className="text-orange1">Details</p>
                </li>
              </ol>
            </nav>
          </section>

          <div className="pb-4 bg-white dark:bg-gray-900 flex flex-wrap justify-between items-center mb-6">
            <div className="w-full sm:w-auto mb-4 sm:mb-0">
              <h1 className="text-2xl font-bold text-gray-800">
                {productData.product_units_name?.replace(/_/g, ' ').toUpperCase() || "Product Details"}
              </h1>
              <p className="text-gray-600 text-sm mt-1">
                Product Code: <span className="font-semibold">{productData.product_code}</span>
              </p>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="w-full sm:w-auto text-dGrey3 bg-darkGreen3 hover:bg-green-700 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center transition-colors"
            >
              <span className="flex justify-center sm:justify-start items-center">
                <ArrowLeftSquareFill className="mr-2 w-5 h-5" />
                Go Back
              </span>
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Information */}
            <InfoCard title="Basic Information">
              <InfoRow
                label="Product Name"
                value={productData.product_units_name?.replace(/_/g, ' ').toUpperCase()}
              />
              <InfoRow label="Product Code" value={productData.product_code?.toUpperCase()} />
              <InfoRow label="Category" value={productData.product_categories_name?.toUpperCase()} />
              <InfoRow label="Unit" value={CurrencyFormatter(productData.unit || "0.00")} />
              <InfoRow
                label="Base Price"
                value={CurrencyFormatter(productData.base_price || "0.00")}
                highlight
              />
              <InfoRow
                label="Status"
                value={
                  <span className={productData.is_enabled ? "text-dash font-semibold" : "text-dash3 font-semibold"}>
                    {productData.is_enabled ? "ACTIVE" : "INACTIVE"}
                  </span>
                }
              />
            </InfoCard>

            {/* Stock Information */}
            <InfoCard title="Stock Information">
              <InfoRow label="Opening Stock" value={productData.opening_stock || 0} />
              <InfoRow label="Current Stock" value={productData.current_stock || 0} highlight />
              {/* <InfoRow label="Reorder Level" value={productData.reorder_level || 0} /> */}
              <InfoRow label="Total Purchases" value={productData.total_purchases} />
              <InfoRow label="Total Sales" value={productData.total_sales || 0} />
              <InfoRow
                label="Stock Status"
                value={
                  <span className={productData.current_stock <= productData.reorder_level ? "text-dash3 font-semibold" : "text-dash font-semibold"}>
                    {productData.current_stock <= productData.reorder_level ? "LOW STOCK" : "GOOD"}
                  </span>
                }
              />
            </InfoCard>

            {/* Financial Information */}
            <InfoCard title="Financial Information">
              <InfoRow
                label="Total Purchase Amount"
                value={CurrencyFormatter(productData.total_purchase_amount || "0.00")}
              />
              <InfoRow
                label="Total Sales Amount"
                value={CurrencyFormatter(productData.total_sales_amount || "0.00")}
              />
              <InfoRow
                label="Profit/Loss"
                value={CurrencyFormatter(
                  parseFloat(productData.total_sales_amount || 0) - parseFloat(productData.total_purchase_amount || 0)
                )}
                highlight
              />
            </InfoCard>

            {/* Dates Information */}
            <InfoCard title="Date Information">
              {/* <InfoRow label="Date Created" value={formatDate(productData.created_at)} />
              <InfoRow label="Last Updated" value={formatDate(productData.updated_at)} /> */}
              <InfoRow label="Last Restocked" value={formatDate(productData.last_restocked_date)} />
              <InfoRow label="Last Sale Date" value={formatDate(productData.last_sale_date)} />
            </InfoCard>
          </div>


          {/* Quantity Pricing */}
          {productData.quantity_pricing && productData.quantity_pricing.length > 0 &&
            productData.quantity_pricing.some(p => p.min_quantity > 0 || p.max_quantity > 0 || p.price > 0) && (
              <InfoCard title="Quantity Pricing">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-darkGreen3 text-lGrey border-b border-lGrey">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Min Quantity</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Max Quantity</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productData.quantity_pricing.map((pricing, index) => (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 text-gray-600">{pricing.min_quantity || "0"}</td>
                          <td className="px-4 py-3 text-gray-600">{pricing.max_quantity || "Unlimited"}</td>
                          <td className="px-4 py-3 text-gray-800 font-semibold">
                            {CurrencyFormatter(pricing.price)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </InfoCard>
            )}

          {/* Commission Categories */}
          {/* {productData.commission_categories && productData.commission_categories.length > 0 && (
            <InfoCard title="Commission Categories">
              <div className="space-y-2">
                {productData.commission_categories.map((category, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded border border-lGrey text-sm">
                    <pre className="text-gray-700 whitespace-pre-wrap break-words">
                      {JSON.stringify(category, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            </InfoCard>
          )} */}

          {productData.commission_categories && productData.commission_categories.length > 0 && (
            <InfoCard title="Commission Categories">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-darkGreen3 text-lGrey border-b border-lGrey">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Name</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Description</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Discount %</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Pricing Rules</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productData.commission_categories.map((category, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-gray-600">{category.name.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-3 text-gray-600">{category.description || "NOT AVAILABLE"}</td>
                        <td className="px-4 py-3 text-gray-600">{category.discount_percentage || "0.00"}%</td>
                        <td className="px-4 py-3 text-gray-600">
                          {category.pricing_rules ? (
                            <table className="w-full text-xs border border-lGrey">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-2 py-1 text-left">Quantity</th>
                                  <th className="px-2 py-1 text-left">Type</th>
                                  <th className="px-2 py-1 text-left">Discount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(category.pricing_rules).map(([threshold, rule], i) => (
                                  <tr key={i} className="border-t">
                                    <td className="px-2 py-1">{threshold}</td>
                                    <td className="px-2 py-1 capitalize">{rule.type}</td>
                                    <td className="px-2 py-1">{rule.value || 0}%</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            "No rules"
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {category.is_active ? (
                            <span className="text-dash font-semibold">Active</span>
                          ) : (
                            <span className="text-dash3 font-semibold">Inactive</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </InfoCard>
          )}


          {/* Description - Full Width */}
          <InfoCard title="Description">
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-700 text-sm mb-2">Product Description:</h3>
                <p className="text-gray-600 text-sm">{productData.description || "No description available"}</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 text-sm mb-2">Category Description:</h3>
                <p className="text-gray-600 text-sm">{productData.product_categories_description || "No category description available"}</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 text-sm mb-2">Unit Description:</h3>
                <p className="text-gray-600 text-sm">{productData.product_unit_description || "No unit description available"}</p>
              </div>
            </div>
          </InfoCard>
        </div>
      </MainLayouts >
    </div >
  );
};

export default BranchProductDetails;